import React, { useEffect, useRef, useState } from 'react'
import {
  components,
  type InputActionMeta,
  type MenuListProps,
} from 'react-select'
import { type useVirtualizer } from '@tanstack/react-virtual'

import {
  extractElementsFromOptionsArray,
  type OptionsArray,
  type SelectGroupType,
} from '../SelectHelpers'
import styles from '../_select.module.scss'
import {
  Button,
  hasValue,
  SearchBar,
  useFocus,
  usePrevious,
} from '../../../../module'
import { Virtual } from '../../../Virtual/Virtual'
import NoOptionsMessage from './NoOptionsMessage'
import ScrollingContainer from '../../../ScrollingContainer/ScrollingContainer'
import { type SelectProps } from '../Select'
import SelectLoading from '../SelectLoading'

const MenuList = <OptionType,>(props: MenuListProps<OptionType>) => {
  const { selectProps } = props,
    {
      inputValue,
      onInputChange,
      onMenuInputFocus,
      isLoading,
      optionKeyName,
      searchBarProps,
      footerButtonProps,
      onMenuClose,
      menuRef,
      hasMore,
      getData,
    } = selectProps,
    rows = props?.children as React.JSX.Element[]

  const flattenedRows = !isLoading ? extractElementsFromOptionsArray(rows) : []

  // We do not want to depend on component names. Keep this condition simple.
  const isNoOptionsMessage = !Array.isArray(flattenedRows)

  const selectedIndex = isNoOptionsMessage
    ? -1
    : flattenedRows.findIndex(
        (row) =>
          (row.props?.data as OptionType)?.[
            optionKeyName as keyof OptionType
          ] ===
          (selectProps?.value as OptionType)?.[
            optionKeyName as keyof OptionType
          ],
      )

  return (
    <div ref={menuRef}>
      <Virtual count={flattenedRows?.length}>
        {({ virtualizer, virtualizedParentRef }) => {
          return (
            <MenuListContainer
              virtualizer={virtualizer}
              virtualizedParentRef={virtualizedParentRef}
              // @ts-expect-error props type error
              props={props}
              inputValue={inputValue}
              onInputChange={onInputChange}
              onMenuInputFocus={onMenuInputFocus}
              flattenedRows={flattenedRows}
              selectedIndex={selectedIndex}
              isNoOptionsMessage={isNoOptionsMessage}
              searchBarProps={searchBarProps}
              footerButtonProps={footerButtonProps}
              onMenuClose={onMenuClose}
              isLoading={isLoading}
              hasMore={hasMore}
              getData={getData}
            />
          )
        }}
      </Virtual>
    </div>
  )
}

export default MenuList

const MenuListContainer = <OptionType,>({
  virtualizer,
  virtualizedParentRef,
  props,
  inputValue,
  onInputChange,
  onMenuInputFocus,
  flattenedRows,
  selectedIndex,
  isNoOptionsMessage,
  searchBarProps,
  footerButtonProps,
  onMenuClose,
  isLoading,
  hasMore = false,
  getData = () => undefined,
}: {
  virtualizer: ReturnType<typeof useVirtualizer>
  virtualizedParentRef: React.MutableRefObject<HTMLDivElement | null>
  props: MenuListProps<OptionType>
  inputValue: string
  onInputChange: (newValue: string, actionMeta: InputActionMeta) => void
  onMenuInputFocus: (() => void) | undefined
  flattenedRows: OptionsArray<OptionType>
  selectedIndex: number
  isNoOptionsMessage: boolean
  searchBarProps?: SelectProps<OptionType>['searchBarProps']
  footerButtonProps?: SelectProps<OptionType>['footerButtonProps']
  onMenuClose: () => void
  isLoading: boolean
  hasMore?: boolean
  getData?: () => void
}) => {
  const infiniteLoadRef = useRef<HTMLDivElement>(null)
  const groupOptions = props.options as SelectGroupType<OptionType>[],
    hasGroups = 'options' in (groupOptions?.[0] ?? {}),
    options = hasGroups
      ? groupOptions.flatMap((group) => group.options)
      : props.options,
    searchBarRef = useFocus(props.selectProps.menuIsOpen),
    previousInputValue = usePrevious(inputValue) as string
  const [showGradients, setShowGradients] = useState(false),
    containerScrollHeight = virtualizer.getTotalSize(),
    containerHeight = virtualizedParentRef.current?.clientHeight ?? 0,
    showSearchBar = hasValue(searchBarProps)
      ? searchBarProps?.showSearchBar
      : options.length > 10,
    // Flag to scroll to the top of the list when the search changes, waiting
    // for there to be elements to scroll to (in the scenario of loading)
    [shouldScrollToTop, setShouldScrollToTop] = useState(false)

  useEffect(() => {
    if (typeof containerHeight === 'number') {
      setShowGradients(containerScrollHeight > containerHeight)
    }
  }, [containerHeight, containerScrollHeight])

  useEffect(() => {
    selectedIndex !== -1 &&
      virtualizer.scrollToIndex(selectedIndex, { align: 'start' })
  }, [selectedIndex, virtualizer])

  // Scroll to top when the search changes
  useEffect(() => {
    if (previousInputValue !== inputValue) {
      setShouldScrollToTop(true)
    }
    // Sometimes the search changes with nothing to scroll to (loading state).
    // This flag allows us to wait for the loading to stop before scrolling
    if (shouldScrollToTop && flattenedRows.length > 0) {
      virtualizer.scrollToIndex(0, { align: 'start' })
      setShouldScrollToTop(false)
    }
  }, [
    inputValue,
    previousInputValue,
    virtualizer,
    flattenedRows,
    isLoading,
    shouldScrollToTop,
  ])

  useEffect(() => {
    if (!isLoading && infiniteLoadRef.current && hasMore) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              getData()
            }
          })
        },
        { threshold: 0.3 },
      )

      observer.observe(infiniteLoadRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [hasMore, getData, isLoading])

  return (
    <div className={styles.menuListContainer}>
      {showSearchBar ? (
        <div
          className={styles.searchContainer}
          onKeyDown={(e) => {
            // Prevent the select component from handling keyboard events when the search bar is focused
            e.stopPropagation()
          }}
        >
          <SearchBar
            minWidth={100}
            {...searchBarProps}
            value={inputValue}
            onChange={(value) => {
              onInputChange(value, {
                action: 'input-change',
                prevInputValue: '',
              })
              searchBarProps?.onChange?.(value)
            }}
            onFocus={() => {
              onMenuInputFocus?.()
              searchBarProps?.onFocus?.()
            }}
            ref={searchBarRef}
          />
        </div>
      ) : null}
      {isLoading ? (
        <div className={styles.listLoadingContainer}>
          <SelectLoading initialLoading />
        </div>
      ) : (
        <>
          {isNoOptionsMessage ? (
            <NoOptionsMessage {...props} />
          ) : (
            <ScrollingContainer
              position='absolute'
              hideGradients={!showGradients}
              height={containerHeight}
            >
              <components.MenuList {...props} innerRef={virtualizedParentRef}>
                <Virtual.List virtualizer={virtualizer}>
                  {({ index, measureElement }) => (
                    <div ref={measureElement} data-index={index}>
                      {flattenedRows[index]}
                    </div>
                  )}
                </Virtual.List>
                {hasMore ? (
                  <div
                    className={styles.listLoadingContainer}
                    ref={infiniteLoadRef}
                  >
                    <SelectLoading />
                  </div>
                ) : null}
              </components.MenuList>
            </ScrollingContainer>
          )}
          {footerButtonProps ? (
            <div className={styles.footerContainer}>
              <Button
                as='button'
                styleType='text-blue'
                onClick={() => {
                  footerButtonProps.callout()
                  onMenuClose()
                }}
              >
                {footerButtonProps.text}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
